import React from 'react';
import { Skeleton } from 'antd';
import '../../pages/OrganizationRolesPage.css';

interface RolesGridSkeletonProps {
  /** How many placeholder cards to render. */
  cards?: number;
}

/** Deterministic-but-varied permission pill widths per card. */
const PERM_WIDTHS = [
  [72, 56, 88, 64],
  [60, 80, 52],
  [88, 64, 72],
  [56, 92, 60, 76],
  [70, 58],
  [84, 64, 50, 78],
];

/**
 * Loading placeholder for the roles grid in {@link OrganizationRolesPage}.
 * Reuses the SAME `.org-roles-grid` / `.org-role-card` classes so the grid
 * tracks (auto-fill, minmax(280px, 1fr)), card padding, border and the
 * absolutely-positioned count pill all match the real cards exactly.
 */
export function RolesGridSkeleton({ cards = 6 }: RolesGridSkeletonProps) {
  return (
    <div className="org-roles-grid" aria-busy>
      {Array.from({ length: cards }).map((_, i) => {
        const perms = PERM_WIDTHS[i % PERM_WIDTHS.length];
        return (
          <div className="org-role-card" key={`role-skeleton-${i}`}>
            {/* Count pill — absolute, top-right (matches .org-role-card__count) */}
            <Skeleton.Button
              active
              size="small"
              shape="round"
              style={{ position: 'absolute', top: 16, right: 16, width: 60, minWidth: 60, height: 22 }}
            />

            {/* Role name */}
            <div className="org-role-card__header">
              <Skeleton.Input active size="small" style={{ width: 120, minWidth: 120, height: 18 }} />
            </div>

            {/* Permission pills */}
            <div className="org-role-card__permissions">
              {perms.map((w, j) => (
                <Skeleton.Button
                  key={j}
                  active
                  size="small"
                  shape="round"
                  style={{ width: w, minWidth: w, height: 24 }}
                />
              ))}
            </div>

            {/* Last edited line — pinned to the bottom via margin-top:auto */}
            <Skeleton.Input
              active
              size="small"
              style={{ width: 110, minWidth: 110, height: 12, marginTop: 'auto' }}
            />
          </div>
        );
      })}
    </div>
  );
}
